import { POSTHOG_SCHEMA_VERSION } from '@/lib/posthogIdentity';
import { trackEvent } from '@/lib/trackEvent';

const VIEWED_DWELL_MS = 1_000;
const VIEWED_THRESHOLD = 0.5;
const SECTION_SELECTOR = '[data-report-section], [data-report-actionable-insight]';

type SectionViewedOptions = {
  reportId: string;
  root?: ParentNode;
  selector?: string;
};

const resolveSectionName = (element: HTMLElement): string | undefined => {
  const section = element.closest('[data-report-section]') as HTMLElement | null;
  return (
    element.dataset.reportSectionName ||
    section?.dataset.reportSectionName ||
    element.dataset.reportSection ||
    section?.dataset.reportSection ||
    undefined
  );
};

export const trackSectionViewed = (
  element: HTMLElement,
  reportId: string
): boolean => {
  const sectionName = resolveSectionName(element);
  if (!sectionName || !reportId) return false;
  const section = element.closest('[data-report-section]') as HTMLElement | null;
  const specialName =
    element.dataset.reportSpecialName || section?.dataset.reportSpecialName || undefined;
  const isActionableInsight =
    element.dataset.reportActionableInsight === 'true' ||
    Boolean(element.closest('[data-report-actionable-insight="true"]'));
  return trackEvent('section_viewed', {
    report_id: reportId,
    schema_version: POSTHOG_SCHEMA_VERSION,
    section_name: sectionName,
    ...(specialName ? { special_name: specialName } : {}),
    is_actionable_insight: isActionableInsight,
  });
};

const bindViewedElement = (
  element: HTMLElement,
  reportId: string
): (() => void) => {
  if (!resolveSectionName(element)) return () => undefined;

  let timer: ReturnType<typeof setTimeout> | undefined;
  let observer: IntersectionObserver | undefined;

  const clearTimer = () => {
    if (!timer) return;
    clearTimeout(timer);
    timer = undefined;
  };

  const disconnect = () => {
    clearTimer();
    observer?.disconnect();
    observer = undefined;
  };

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.target !== element) return;

        if (!entry.isIntersecting || entry.intersectionRatio < VIEWED_THRESHOLD) {
          clearTimer();
          return;
        }

        if (timer) return;

        timer = setTimeout(() => {
          timer = undefined;
          const tracked = trackSectionViewed(element, reportId);
          if (tracked) {
            disconnect();
          }
        }, VIEWED_DWELL_MS);
      });
    },
    { threshold: [0, VIEWED_THRESHOLD, 0.75, 1] }
  );

  observer.observe(element);

  return disconnect;
};

const scan = (
  node: ParentNode,
  selector: string,
  attach: (element: HTMLElement) => void
) => {
  if (node instanceof HTMLElement && node.matches(selector)) {
    attach(node);
  }
  node.querySelectorAll?.(selector).forEach((element) => {
    attach(element as HTMLElement);
  });
};

export const startSectionViewedTracking = (
  options: SectionViewedOptions
): (() => void) => {
  if (
    !options.reportId ||
    typeof window === 'undefined' ||
    typeof IntersectionObserver === 'undefined'
  ) {
    return () => undefined;
  }

  const root = options.root ?? document;
  const selector = options.selector ?? SECTION_SELECTOR;
  const reportId = options.reportId;
  const bound = new Set<HTMLElement>();
  const cleanups: Array<() => void> = [];

  const attach = (element: HTMLElement) => {
    if (bound.has(element)) return;
    bound.add(element);
    cleanups.push(bindViewedElement(element, reportId));
  };

  scan(root, selector, attach);

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          scan(node, selector, attach);
        }
      });
    });
  });

  const observeTarget =
    root instanceof Document ? root.documentElement : (root as Element);
  mutationObserver.observe(observeTarget, { childList: true, subtree: true });

  return () => {
    mutationObserver.disconnect();
    cleanups.forEach((stop) => stop());
  };
};
